/**
 * The search field and the list/cards view toggle.
 *
 * Search is a FILTER on the current listing, not a separate destination: it
 * searches recursively inside the open folder, or everything under the root,
 * which is what the placeholder spells out so the scope is never a guess.
 *
 * Escape clears — immediately, not on the debounce, because waiting 400 ms for
 * the full listing to come back reads as a hang.
 */

import { LayoutGrid, List, Search as SearchIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { KbdChip } from '@/shared/components/KbdChip.jsx';
import { SearchHintCallout } from '@/shared/components/SearchHintCallout.jsx';
import { openCommandPalette } from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { cn } from '@/lib/utils';

const TOGGLE_BUTTON = 'p-1.5 rounded-[4px] transition-colors cursor-pointer';
const TOGGLE_ACTIVE = 'bg-white shadow-sm text-foreground dark:bg-background';
const TOGGLE_IDLE = 'text-muted-foreground hover:text-foreground';

export function ListingToolbar({
  searchValue,
  onSearchChange,
  onClearSearch,
  folderName,
  inFolder,
  viewMode,
  onViewModeChange,
}) {
  const { t } = useTranslation(['common', 'mainPage', 'search']);

  const placeholder = !inFolder
    ? t('mainPage:searchAllDocs') || 'Search all documents…'
    : folderName
      ? t('mainPage:searchInFolder', { folder: folderName }) ||
        `Search “${folderName}” and subfolders`
      : t('mainPage:searchHere') || 'Search this folder and subfolders';

  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="relative flex-1">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && searchValue) {
              e.preventDefault();
              onClearSearch();
            }
          }}
          className={`pl-9 ${searchValue ? 'pr-9' : 'pr-16'} bg-muted/50 border-border rounded-[4px]`}
        />
        {/* The global-search shortcut, taught where people already look for
            search. This input filters the LISTING; the chip opens the palette
            that searches everything — the tooltip says so. Mutually exclusive
            with the clear button (same slot). */}
        {!searchValue && (
          <button
            type="button"
            onClick={() => openCommandPalette()}
            title={t('search:everywhereHint')}
            aria-label={t('search:everywhere')}
            className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer transition hover:scale-105"
          >
            <KbdChip>{shortcutLabel()}</KbdChip>
          </button>
        )}
        {/* First-run coach mark, anchored under the keycap it explains. */}
        <SearchHintCallout className="absolute right-0 top-full z-30 mt-2" />
        {searchValue && (
          <button
            type="button"
            onClick={onClearSearch}
            aria-label={t('common:clear') || 'Clear search'}
            title={t('common:clear') || 'Clear search'}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 rounded-[4px] bg-muted/60 p-1 shrink-0">
        <button
          type="button"
          onClick={() => onViewModeChange('list')}
          aria-pressed={viewMode === 'list'}
          className={cn(TOGGLE_BUTTON, viewMode === 'list' ? TOGGLE_ACTIVE : TOGGLE_IDLE)}
          title={t('mainPage:view.list')}
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onViewModeChange('cards')}
          aria-pressed={viewMode === 'cards'}
          className={cn(TOGGLE_BUTTON, viewMode === 'cards' ? TOGGLE_ACTIVE : TOGGLE_IDLE)}
          title={t('mainPage:view.cards')}
        >
          <LayoutGrid className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
